import { Suspense, useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router";
import { Bell, LogOut } from "lucide-react";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { cn, relative } from "../lib/format";
import type { Notification, Role } from "../lib/types";
import { Loading } from "./ui";

const NAV: Record<Role, { to: string; label: string; end?: boolean }[]> = {
  alumno: [
    { to: "/", label: "Mi semana", end: true },
    { to: "/obra", label: "Escribir" },
    { to: "/sesiones", label: "Sesiones" },
    { to: "/versiones", label: "Versiones" },
    { to: "/analisis", label: "Análisis" },
  ],
  profesor: [
    { to: "/profesor", label: "Revisiones", end: true },
    { to: "/profesor/grupos", label: "Mis grupos" },
    { to: "/profesor/clases", label: "Clases" },
  ],
  coordinacion: [
    { to: "/coordinacion", label: "Tablero", end: true },
    { to: "/coordinacion/generaciones", label: "Generaciones" },
    { to: "/coordinacion/personas", label: "Personas" },
    { to: "/coordinacion/solicitudes", label: "Solicitudes" },
    { to: "/coordinacion/ajustes", label: "Ajustes" },
  ],
  editorial: [{ to: "/coordinacion", label: "Tablero", end: true }],
};

export function Logo({ className }: { className?: string }) {
  return (
    <span className={cn("flex items-center gap-[10px]", className)}>
      <img src="/ele-logo.svg" alt="" className="h-[22px] w-auto" />
      <span className="font-serif text-[20px] leading-none text-carbon-ink">PEI</span>
    </span>
  );
}

function Notifications() {
  const { profile } = useAuth();
  const [items, setItems] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!profile) return;
    supabase
      .from("notifications")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(20)
      .then(({ data }) => setItems((data as Notification[]) ?? []));
    const ch = supabase
      .channel(`notif-${profile.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${profile.id}` }, (p) =>
        setItems((x) => [p.new as Notification, ...x].slice(0, 20)),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [profile]);

  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const unread = items.filter((n) => !n.read_at).length;

  async function openItem(n: Notification) {
    setOpen(false);
    if (!n.read_at) {
      await supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("id", n.id);
      setItems((x) => x.map((i) => (i.id === n.id ? { ...i, read_at: new Date().toISOString() } : i)));
    }
    if (n.link) navigate(n.link);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-8 text-graphite hover:bg-soft-stone hover:text-carbon-ink"
        aria-label={unread ? `Avisos, ${unread} sin leer` : "Avisos"}
      >
        <Bell size={18} />
        {unread > 0 && <span className="absolute right-[6px] top-[6px] h-[7px] w-[7px] rounded-full bg-clay" />}
      </button>
      {open && (
        <div className="absolute right-0 top-[44px] z-40 w-[min(340px,calc(100vw-32px))] rounded-2xl border border-chalk bg-paper-white p-8 shadow-lg">
          {items.length === 0 && <p className="p-16 text-body text-ashen">Sin avisos por ahora.</p>}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => openItem(n)}
              className="flex w-full flex-col gap-[2px] rounded-lg px-[12px] py-[10px] text-left hover:bg-bone-parchment"
            >
              <span className={cn("text-body", n.read_at ? "text-graphite" : "font-w550 text-carbon-ink")}>{n.title}</span>
              {n.body && <span className="text-xs2 text-ashen line-clamp-2">{n.body}</span>}
              <span className="text-caption text-pebble">{relative(n.created_at)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function AppShell() {
  const { profile, signOut } = useAuth();
  const links = NAV[profile?.role ?? "alumno"];
  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-30 border-b border-chalk bg-bone-parchment/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center gap-x-24 gap-y-8 px-16 py-[11px] sm:px-24">
          <Link to="/" className="shrink-0" aria-label="Inicio">
            <Logo />
          </Link>
          <nav className="order-last -mx-8 flex w-full flex-wrap items-center gap-[2px] sm:order-none sm:mx-0 sm:w-auto sm:flex-1 sm:flex-nowrap sm:overflow-x-auto" aria-label="Principal">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  cn(
                    "rounded-lg px-[12px] py-[6px] text-base15 font-medium whitespace-nowrap no-underline transition-colors",
                    isActive ? "bg-soft-stone text-carbon-ink" : "text-graphite hover:text-carbon-ink",
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-8 sm:ml-0">
          <Notifications />
          <div className="hidden sm:flex items-center gap-8">
            <Link to={profile?.role === "alumno" ? "/ajustes" : "#"} className="text-body text-graphite no-underline max-w-[160px] truncate">
              {profile?.full_name}
            </Link>
          </div>
          <button onClick={signOut} className="rounded-lg p-8 text-graphite hover:bg-soft-stone hover:text-carbon-ink" aria-label="Cerrar sesión">
            <LogOut size={18} />
          </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1200px] px-16 py-24 sm:px-24 sm:py-40">
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
